// Server-synced clock for tick estimation
// Tracks server time offset using periodic sync messages

import { TICK_RATE, TICK_DURATION_MS } from './config';

const PING_TIMEOUT_MS = 10000;
const MAX_PENDING_PINGS = 8;

export class GameClock {
  private serverTimeOffset = 0; // local time + offset = estimated server time
  private synced = false;
  private syncWarnEmitted = false;
  private rtt = 0;
  private rttSamples: number[] = [];
  private rttSum = 0;
  private lastPingSent = 0;
  private pingSequence = 0;
  private pendingPings: Map<number, number> = new Map();
  private serverTick = 0;
  private localTickAccumulator = 0;

  /**
   * Send a ping — returns the sequence number to track
   */
  sendPing(): { sequence: number; timestamp: number } {
    const seq = this.pingSequence++;
    const now = performance.now();
    this.prunePendingPings(now);
    if (this.pendingPings.size >= MAX_PENDING_PINGS) {
      const oldestSeq = this.pendingPings.keys().next().value;
      if (oldestSeq !== undefined) {
        this.pendingPings.delete(oldestSeq);
      }
    }
    this.pendingPings.set(seq, now);
    this.lastPingSent = now;
    return { sequence: seq, timestamp: now };
  }

  /**
   * Process a pong response from server
   */
  receivePong(sequence: number, serverTimestamp: number): void {
    this.prunePendingPings(performance.now());
    const sentAt = this.pendingPings.get(sequence);
    if (sentAt === undefined) return;

    this.pendingPings.delete(sequence);

    const now = performance.now();
    const roundTrip = now - sentAt;
    const oneWay = roundTrip / 2;

    // Update RTT with running sum (O(1) instead of O(N) reduce)
    this.rttSamples.push(roundTrip);
    this.rttSum += roundTrip;
    if (this.rttSamples.length > 20) {
      this.rttSum -= this.rttSamples.shift()!;
    }

    this.rtt = this.rttSum / this.rttSamples.length;

    // Estimate server time offset
    // serverTimestamp was taken at sentAt + oneWay
    // so server time = serverTimestamp + (now - sentAt - oneWay) = serverTimestamp + oneWay
    this.serverTimeOffset = serverTimestamp - now + oneWay;
    this.synced = true;
  }

  /**
   * Update from a server snapshot tick
   */
  updateServerTick(tick: number): void {
    this.serverTick = tick;
    // Reset local tick accumulator — authoritative server tick takes over,
    // stale accumulated dt would cause drift on the next advanceTick call.
    this.localTickAccumulator = 0;
  }

  /**
   * Get estimated current server time in ms.
   * Returns local time if clock has not yet synced with server.
   */
  getServerTime(): number {
    if (!this.synced) {
      if (!this.syncWarnEmitted) {
        console.warn('GameClock: getServerTime() called before first pong — returning local time');
        this.syncWarnEmitted = true;
      }
      return performance.now();
    }
    return performance.now() + this.serverTimeOffset;
  }

  /**
   * Whether the clock has synced with the server at least once.
   */
  isSynced(): boolean {
    return this.synced;
  }

  /**
   * Get estimated current server tick
   */
  getEstimatedServerTick(): number {
    return this.serverTick;
  }

  /**
   * Get the render time for interpolation (server time - interp delay)
   */
  getRenderTime(interpTicks: number = 2): number {
    return this.getServerTime() - interpTicks * TICK_DURATION_MS;
  }

  /**
   * Current RTT in ms
   */
  getRTT(): number {
    return this.rtt;
  }

  /**
   * Should we send a ping? (every 1 second)
   */
  shouldPing(): boolean {
    return performance.now() - this.lastPingSent > 1000;
  }

  /**
   * Advance local tick estimation
   */
  advanceTick(dt: number): void {
    this.localTickAccumulator += dt * 1000;
    while (this.localTickAccumulator >= TICK_DURATION_MS) {
      this.serverTick++;
      this.localTickAccumulator -= TICK_DURATION_MS;
    }
  }

  private prunePendingPings(now: number): void {
    for (const [sequence, sentAt] of this.pendingPings) {
      if (now - sentAt <= PING_TIMEOUT_MS) {
        break;
      }
      this.pendingPings.delete(sequence);
    }
  }
}
